import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Avatar,
  AvatarGroup,
  Button,
  Card,
  CardBody,
  Divider,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useToast,
  Badge,
  useColorModeValue,
  VStack,
  HStack,
} from "@chakra-ui/react";
import CatNavbar from "./CatNavbar";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  CloseIcon,
  DeleteIcon,
  HamburgerIcon,
  ViewIcon,
  ChatIcon,
} from "@chakra-ui/icons";
import { FaRupeeSign } from "react-icons/fa";
import Inbox from "./ChatComponents/Inbox";
import FetchChat from "./ChatComponents/FetchChat";
import SendChat from "./ChatComponents/SendChat";
import Loading from "./resources/Loading";

export default function MyChat() {
  const { id, useremail } = useParams();
  const toast = useToast();
  const authToken = localStorage.getItem("authToken");
  const authemail = localStorage.getItem("authemail");

  const [product, setProduct] = useState({});
  const [profileData, setProfileData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [ChatScreen, setChatScreen] = useState(false);
  
  // Move all useColorModeValue hooks outside of conditional rendering
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const emptyStateBg = useColorModeValue("green.50", "green.900");

  useEffect(() => {
    if (id && useremail) {
      const fetchData = async () => {
        setChatScreen(true);
        try {
          const response = await axios.post(
            `https://api.agromanch.com/previewad/${id}`,
            {},
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          );
          setProduct(response.data.product);
          setIsLoading(false); // Set loading state to false when data is fetched successfully
        } catch (error) {
          setChatScreen(false);
        }
      };
      fetchData();
      if (useremail === authemail) {
        setChatScreen(false);
      } else {
        setChatScreen(true);
        try{
          axios
            .get(`https://api.agromanch.com/profilesearch?useremail=${useremail}`)
            .then((response) => {
              setProfileData(response.data);
              setIsLoading(false);
            })
            .catch((error) => {
              setIsLoading(false);
              setChatScreen(false);
            });
          }
          catch{
            setChatScreen(false);
          }
      }

    } else {
      setIsLoading(false);
    }
  }, [id, useremail, authToken, product.useremail]);

  if (isLoading) {
    return <Loading />;
  }

  const handleDelete = () => {
    setIsLoading(true);
    axios
      .post(
        `https://api.agromanch.com/deletechat/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      )
      .then((response) => {
        setIsLoading(false);
        setChatScreen(false);
        toast({
          title: "Chat Deleted",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      })
      .catch((error) => {
        setIsLoading(false);
        toast({
          title: "No Chats Found",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });
  };

  return (
    <Box bg={pageBg} minH="100vh">
      <Container maxW="container.xl" py={4}>
        <Heading as="h2" size="lg" mb={4}>Messages</Heading>
        <Flex direction={{ base: "column", md: "row" }} gap={4}>
          {/* Left sidebar - Chat list */}
          <Box 
            width={{ base: "100%", md: "320px" }} 
            bg={bgColor} 
            borderRadius="lg" 
            overflow="hidden"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
            mb={{ base: 4, md: 0 }}
          >
            <Inbox />
          </Box>

          {/* Right side - Chat area */}
          <Box 
            flex="1" 
            bg={bgColor} 
            borderRadius="lg" 
            overflow="hidden"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            {ChatScreen ? (
              <VStack spacing={0} h="100%" align="stretch">
                {/* Chat header */}
                <Box 
                  p={3} 
                  borderBottomWidth="1px" 
                  borderColor={borderColor}
                  bg={headerBg}
                >
                  <Flex justify="space-between" align="center">
                    <Link
                      to={`/profile/${useremail}`}
                    >
                      <HStack spacing={3}>
                        <AvatarGroup size="sm" max={2}>
                          <Avatar 
                            src={product.productpic1} 
                            name={product.title}
                            border="2px solid white"
                          />
                          <Avatar 
                            src={profileData.picture} 
                            name={profileData.name}
                            border="2px solid white"
                          />
                        </AvatarGroup>
                        <Box>
                          <Text fontWeight="medium" fontSize="sm">{profileData.name}</Text>
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {product.title}
                          </Text>
                        </Box>
                      </HStack>
                    </Link>
                    
                    <HStack spacing={2}>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label="Options"
                          icon={<HamburgerIcon />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem
                            icon={<DeleteIcon color="red.500" />}
                            onClick={handleDelete}
                          >
                            Delete Chat
                          </MenuItem>
                          <MenuItem
                            as={Link}
                            to={`/preview_ad/${id}`}
                            icon={<ViewIcon color="green.500" />}
                          >
                            View Product
                          </MenuItem>
                        </MenuList>
                      </Menu>
                      
                      <IconButton
                        size="sm"
                        icon={<CloseIcon />}
                        onClick={() => setChatScreen(false)}
                        variant="ghost"
                      />
                    </HStack>
                  </Flex>
                  
                  <Flex mt={2} justify="space-between" align="center" px={1}>
                    <Badge colorScheme="green" variant="subtle">
                      {product.catagory}
                    </Badge>
                    <HStack>
                      <FaRupeeSign size="14px" />
                      <Text fontWeight="bold" fontSize="sm">{product.price}</Text>
                    </HStack>
                  </Flex>
                </Box>
                
                {/* Chat messages */}
                <Box 
                  p={3} 
                  flex="1" 
                  overflowY="auto" 
                  bg={pageBg}
                  maxH={{ base: "50vh", md: "60vh" }}
                >
                  <FetchChat id={id} toData={profileData} to={useremail} />
                </Box>
                
                {/* Message input */}
                <Box p={3} bg={bgColor}>
                  <SendChat id={id} to={useremail} />
                </Box>
              </VStack>
            ) : (
              <Flex
                direction="column"
                alignItems="center"
                justifyContent="center"
                minH="60vh"
                p={6}
              >
                <Box 
                  bg={emptyStateBg} 
                  p={6} 
                  borderRadius="full"
                  mb={4}
                >
                  <ChatIcon boxSize="40px" color="green.500" />
                </Box>
                <Heading size="md" mb={2}>Select a Chat</Heading>
                <Text textAlign="center" color="gray.500">
                  Choose a conversation from the list to start messaging
                </Text>
              </Flex>
            )}
          </Box>
        </Flex>
      </Container>
    </Box>
  );
}
